import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { FALLBACK_CURRENCIES } from "@/hooks/useCurrenciesData";

const CURRENCY_SYMBOL_MAP: Record<string, string> = Object.fromEntries(
  FALLBACK_CURRENCIES.filter((c): c is typeof c & { symbol: string } => c.symbol != null).map(c => [c.code, c.symbol])
);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatDateDDMMYYYY(dateString: string): string {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

export function formatWholeNumber(value: number): string {
  // Safety check for invalid values
  if (value === null || value === undefined || isNaN(value)) {
    value = 0;
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatCurrency(value: number, currency: string): string {
  // Safety check for invalid values
  if (value === null || value === undefined || isNaN(value)) {
    value = 0;
  }
  
  const symbol = CURRENCY_SYMBOL_MAP[currency] || `${currency} `;
  return `${symbol}${formatWholeNumber(value)}`;
}

export function getCurrencySymbol(currencyCode: string): string {
  return CURRENCY_SYMBOL_MAP[currencyCode] || `${currencyCode} `;
}

export function formatCreditLimit(value: number, currency: string): string {
  const symbol = CURRENCY_SYMBOL_MAP[currency] || `${currency} `;
  const roundedValue = Math.round(value);
  return `${symbol}${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(roundedValue)}`;
}

export function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}
